import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button, Card, Col, Row } from 'react-bootstrap';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import { MdWhatsapp } from 'react-icons/md';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PiCrown } from 'react-icons/pi';

type BreadcrumbItem = { text: string; href: string };

type SupportOutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

const Mentoring = () => {
  const [, setBreadcrumbData] = useOutletContext<SupportOutletContext>();
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);

  useEffect(() => {
    setBreadcrumbData([{ text: 'منتورینگ تخصصی میزفا تولز', href: '#' }]);
  }, [setBreadcrumbData]);

  return (
    <div className="mt-5">
      {/* HERO */}
      <div className="mb-4 text-center">
        <h1 className="mb-2 fs-3 fw-bold">منتورینگ تخصصی میزفا تولز</h1>
        <p className="text-700 fs-0">کمک به رشد هوشمندانه کسب‌وکار شما</p>
      </div>

      {/* VIDEO */}
      <Row className="g-3 mb-6">
        <Col xs={12}>
          <div className="mb-3 d-flex justify-content-center">
            <div
              style={{
                position: 'relative',
                aspectRatio: '16/9',
                width: isMdUp ? '70%' : '100%'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  borderRadius: '0.5rem',
                  overflow: 'hidden'
                }}
              >
                <iframe
                  src="https://player.arvancloud.ir/index.html?config=https://mizfa.arvanvod.ir/DYLRwdoaM7/M1d4XNyWD9/origin_config.json"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  title="منتورینگ تخصصی میزفا تولز"
                />
              </div>
            </div>
          </div>
          <p
            className="mb-0 mx-auto text-center text-600"
            style={{ fontSize: '0.9rem' }}
          >
            در این ویدیو، تفاوت دو طرح منتورینگ و فرآیند همکاری توضیح داده شده،
            حتما نگاه کنید
          </p>
        </Col>
      </Row>

      {/* PLANS */}
      <Row className="g-3 mb-3">
        {/* PLAN 1 */}
        <Col xs={12} md={6}>
          <Card className="h-100">
            <Card.Body>
              <h2 className="h5 fw-bold mb-3 d-flex align-items-center">
                <div className="icon-item icon-item-md border rounded-3 shadow-none me-2">
                  <PiCrown size={22} />
                </div>
                طرح منتور سئو
              </h2>
              <p className="mb-3" style={{ fontSize: '0.9rem' }}>
                مناسب برای افرادی که نیاز راهنمایی و پاسخ‌دهی به سوالات مستمر در
                سئو دارند.
              </p>
              <ul
                className="mb-0 lh-lg"
                style={{ fontSize: '0.9rem', paddingRight: '1rem' }}
              >
                <li>گروه خصوصی با منتور</li>
                <li>راهنمایی سریع در تصمیم‌های مهم</li>
                <li>پاسخ دقیق به سوالات و ابهامات</li>
                <li>بررسی روزانه سوالات در گروه</li>
                <li>شفاف‌سازی مسیر سئو</li>
                <li>کاهش اشتباهات و ریسک‌ها</li>
                <li>همراهی مستقیم متخصص سئو</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>

        {/* PLAN 2 */}
        <Col xs={12} md={6}>
          <Card className="h-100">
            <Card.Body>
              <h2
                className="h5 fw-bold mb-3 d-flex align-items-center"
                style={{
                  color: getColor('navbar-vertical-default-link-active-color')
                }}
              >
                <div className="icon-item icon-item-md border rounded-3 shadow-none me-2">
                  <FontAwesomeIcon
                    size="lg"
                    icon={'crown'}
                    color={getColor(
                      'navbar-vertical-default-link-active-color'
                    )}
                  />
                </div>
                طرح منتور ۳۶۰ درجه
              </h2>

              <p className="mb-3" style={{ fontSize: '0.9rem' }}>
                فراتر از سئو، همراهی کامل، پیگیری، تحلیل عمیق، درگیر شدن در مسیر
                کاربر
              </p>
              <ul
                className="mb-0 lh-lg"
                style={{ fontSize: '0.9rem', paddingRight: '1rem' }}
              >
                <li>شامل تمام ویژگی‌های طرح قبلی</li>
                <li> همراهی سه متخصص باتجربه با ۲۰ سال تجربه</li>
                <li>برنامه‌ریزی اختصاصی سایت</li>
                <li>بسته هفتگی برنامه اجرا</li>
                <li>پیگیری روزانه و هفتگی</li>
                <li>یادآور کلندر + نظم‌دهی</li>
                <li>انگیزه‌دهی و نظم در اجرا</li>
                <li>ارائه راهکارهای عملی</li>
                <li>مشاوره برندینگ و دیجیتال</li>
                <li>تحلیل هفتگی نتایج</li>
                <li>کمک در تصمیم‌های مهم مسیر</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* CTA */}
      <Card className="border-0">
        <Card.Body className="text-center">
          <p className="mb-3" style={{ fontSize: '1rem' }}>
            برای دریافت مشاوره یا شروع همکاری از طریق واتس‌اپ پیام دهید
          </p>
          <Button
            onClick={() => {
              window.open('https://wa.me/989300208048', '_blank');
            }}
          >
            <MdWhatsapp fontSize={21} className="me-2" />
            ارتباط در واتس‌اپ
          </Button>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Mentoring;
